import { IUsageEvent } from '@1js/search-types/lib/logging';
import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { InstrumentationParam, InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { KnownWorkloadReferralNames } from '../shared/models/known-workload-referral-names.model';
import { ContextService } from '../shared/shared-context/context.service';

interface FastInstrumentationEvent {
    itemCategory: SearchResultClickType;
    knownArea: KnownAreas;
    searchResultType: SearchResultType;
    event: KnownInstrumentationEvents;
}

const enum  OtherKnownReferralNames {
    People = 'People',
    PDF = 'PDF'
}

const enum SearchResultClickType {
    AutoSuggest = 'Auto_Suggest',
    AutoSuggestHeader = 'Auto_Suggest_Header',
    AutoSuggestImpression = 'Auto_Suggest_Impression',
    AutoSuggestLoaded = 'Auto_Suggest_Loaded',
    Clear = 'Clear',
    FileType = 'File_Type',
    Filter = 'Filter',
    Pagination = 'Pagination',
    PersonProfile = 'Person_Profile',
    Result = 'Result',
    ResultExpansion = 'Result_Expansion',
    ResultClickSubLink = 'Result_Click_Sub_Link',
    SERP = 'SERP',
    SERPImpression = 'SERP_Impression',
    ShowMoreResults = 'Show_More_Results',
    Submit = 'Submit',
    ThisSite = 'This_Site',
    VerticalFilter = 'Vertical_Filter'
}

const enum SearchResultType {
    App = 'App',
    File = 'File',
    Site = 'Site',
    News = 'News',
    Person = 'Person'
}

/**
 * This is the service responsible for logging events originating from the FAST Search UX
 */
@Injectable({
    providedIn: 'root'
})
export class FastSearchUXInstrumentationService {

    private readonly contextToEventMapper: { [context: string]: FastInstrumentationEvent };
    private readonly sessionExpirationTimeInMilliseconds = 60000; // 60 seconds
    private readonly usingSharePointResourceId = !this.contextService.sharedContext.usingOneDriveResourceAsSharePointResource;

    private searchCenterUrlReferral: string;
    private sessionStarted = false;
    private sessionTimeout: ReturnType<typeof setTimeout>;

    constructor(
        private readonly contextService: ContextService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly instrumentationService: InstrumentationService
    ) {
        this.contextToEventMapper = {
            // Logs when the search box is activated
            'ConfigurationDrivenSearchBox.BasicSearchBoxLogger.onActivated': {
                itemCategory: SearchResultClickType.AutoSuggest,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when suggestions are fetched from the server
            'ConfigurationDrivenSearchBox.SuggestionProviderHost.onSuggestionsFetched': {
                itemCategory: SearchResultClickType.AutoSuggest,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchAutoSuggestionResultsFetched
            },
            // Logs when the user presses an app in the auto suggestions
            'ConfigurationDrivenSearchBox.ProvisionedAppSuggestion.onClick': {
                itemCategory: SearchResultClickType.AutoSuggest,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: SearchResultType.App,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a third party app in the auto suggestions
            'ConfigurationDrivenSearchBox.ThirdPartyAppSuggestion.onClick': {
                itemCategory: SearchResultClickType.AutoSuggest,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: SearchResultType.App,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a file in the auto suggestions
            'ConfigurationDrivenSearchBox.FileSuggestion.onClick': {
                itemCategory: SearchResultClickType.AutoSuggest,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: SearchResultType.File,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs the file type when the user presses a file
            'FileClickedWithExtensionCustom': {
                itemCategory: SearchResultClickType.FileType,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.File,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs the file type when the user presses a file
            'FileClickedWithExtension': {
                itemCategory: SearchResultClickType.FileType,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.File,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when user presses the 'x' clear button
            'ConfigurationDrivenSearchBox.SearchBoxClearButton.onClick': {
                itemCategory: SearchResultClickType.Clear,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses the search button
            'ConfigurationDrivenSearchBox.SearchBoxSubmitButton.onClick': {
                itemCategory: SearchResultClickType.Submit,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a site in the auto suggestions
            'ConfigurationDrivenSearchBox.SiteSuggestion.onClick': {
                itemCategory: SearchResultClickType.AutoSuggest,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: SearchResultType.Site,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a person in the auto suggestions
            'ConfigurationDrivenSearchBox.PersonSuggestion.onClick': {
                itemCategory: SearchResultClickType.AutoSuggest,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses 'Show more results'
            'ConfigurationDrivenSearchBox.SubmitSearchSuggestion.onClick': {
                itemCategory: SearchResultClickType.ShowMoreResults,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a header in the auto suggestions
            'ConfigurationDrivenSearchBox.SuggestionProviderHost.onSuggestionHeadingClick': {
                itemCategory: SearchResultClickType.AutoSuggestHeader,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when any suggestion section is loaded (includes a result count for that section)
            'ConfigurationDrivenSearchBox.SuggestionProviderHost.suggest': {
                itemCategory: SearchResultClickType.AutoSuggestLoaded,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchAutoSuggestionResultsFetched
            },
            // Logs when the user presses a document title in the SERP
            'vNextSearchPage.TriageFileSearchResult.onClickTargetOpen': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.File,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a document title in the SERP
            'vNextSearchPage.TriageFileSearchResult.onClick': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.File,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a site title in the SERP
            'vNextSearchPage.TriageSiteSearchResult.onClick': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Site,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a news title in the SERP
            'vNextSearchPage.TriageNewsSearchResult.onClick': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.News,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a person in the SERP
            'vNextSearchPage.FlashinPeopleCard.onPersonClickTargetWrapperTrigger': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a person who modified a file
            'FilesSearchPanel.TriageFileSearchResult.onPersonClickTargetWrapperTrigger': {
                itemCategory: SearchResultClickType.ResultClickSubLink,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a file in the Files tab in the SERP
            'FilesSearchPanel.TriageFileSearchResult.onClick': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.File,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a person in the People tab in the SERP
            'PeopleSearchPanel.ProfileSearchResult.onPersonClickTargetWrapperTrigger': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a news title in the SERP
            'NewsSearchPanel.TriageNewsSearchResult.onClick': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.News,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a person in the SERP
            'vNextSearchPage.FlashinPeopleCard.onClick': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a person who published a site
            'NewsSearchPanel.TriageNewsSearchResult.onPersonClickTargetWrapperTrigger': {
                itemCategory: SearchResultClickType.ResultClickSubLink,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses the image of a person's profile
            'PeopleSearchPanel.ProfileSearchResult.onClick': {
                itemCategory: SearchResultClickType.PersonProfile,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses a site title in the SERP
            'SitesSearchPanel.TriageSiteSearchResult.onClick': {
                itemCategory: SearchResultClickType.Result,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Site,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user expands a file result in the SERP
            'vNextSearchPage.TriageFileSearchResult.onExpand': {
                itemCategory: SearchResultClickType.ResultExpansion,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.File,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user expands a site result in the SERP
            'SitesSearchPanel.TriageSiteSearchResult.onExpand': {
                itemCategory: SearchResultClickType.ResultExpansion,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Site,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user expands a site result in the SERP
            'NewsSearchPanel.TriageNewsSearchResult.onExpand': {
                itemCategory: SearchResultClickType.ResultExpansion,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Person,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user clicks on a sublink within a site result in the SERP
            'SitesSearchPanel.t.onDeepLinkClick': {
                itemCategory: SearchResultClickType.ResultClickSubLink,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Site,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user clicks on an activity link within a site result in the SERP
            'SitesSearchPanel.t.onActivityClick': {
                itemCategory: SearchResultClickType.ResultClickSubLink,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Site,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses the 'Search this site' button on a site
            'SitesSearchPanel.TriageSiteSearchResult.onClickSitesSearchPanel.TriageSiteSearchResultHeroModuleSearchSiteLink.onSubmitSearch': {
                itemCategory: SearchResultClickType.ThisSite,
                knownArea: KnownAreas.SearchResults,
                searchResultType: SearchResultType.Site,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses any vertical header in the SERP
            'VerticalFiltersRoot.VerticalFilters.changeAndLog': {
                itemCategory: SearchResultClickType.VerticalFilter,
                knownArea: KnownAreas.SearchResults,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses any pagination button
            'vNextSearchPage.Pagination.onClick': {
                itemCategory: SearchResultClickType.Pagination,
                knownArea: KnownAreas.SearchResults,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses the filter button
            'RefinerButton.onClick': {
                itemCategory: SearchResultClickType.Filter,
                knownArea: KnownAreas.SearchResults,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchClick
            },
            // Logs when the user presses the filter button
            'VnextSearchPage.Impression': {
                itemCategory: SearchResultClickType.SERP,
                knownArea: KnownAreas.SearchResults,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchResultsFetched
            },
            // AutoSuggestion impression
            'Searchbox.Impression': {
                itemCategory: SearchResultClickType.AutoSuggestImpression,
                knownArea: KnownAreas.SearchSuggestions,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchResultsFetched
            },
            // SERP impression
            'serp_impression': {
                itemCategory: SearchResultClickType.SERPImpression,
                knownArea: KnownAreas.SearchResults,
                searchResultType: null,
                event: KnownInstrumentationEvents.SearchResultsFetched
            }
        };
    }

    public searchLogger(logEvent: IUsageEvent, referral: string = '', referralPlatform?: KnownReferralPlatform, pageNav: boolean = false) {
        const logProperties = logEvent.properties || {};
        const fastEvent = this.contextToEventMapper[logEvent.nameDetail || logEvent.name];
        if (typeof fastEvent === 'undefined') {
            return;
        }

        this.checkForSessionTimeout();

        const params = {};
        params[InstrumentationParam.ItemCategory] = fastEvent.itemCategory;
        params[InstrumentationParam.SearchResultType] = fastEvent.searchResultType;
        params[InstrumentationParam.UsingSharePointResourceId] = this.usingSharePointResourceId.toString();

        if (fastEvent.event === KnownInstrumentationEvents.SearchResultsFetched) {
            try {
                params[InstrumentationParam.SearchItemCount] = logProperties.items ? JSON.parse(logProperties.items).length : null;
            } catch (e) {
                this.globalErrorHandler.handleError(e, ErrorId.cYDfB);
            }
        }

        if (fastEvent.event === KnownInstrumentationEvents.SearchClick) {
            this.instrumentationService
                .logClickEvent(
                    fastEvent.event,
                    `${fastEvent.itemCategory}_${fastEvent.searchResultType}`,
                    fastEvent.knownArea.toString(),
                    referral ? referral : this.getReferralFromEventData(logProperties.eventData, fastEvent),
                    referralPlatform,
                    params,
                    undefined,
                    undefined,
                    pageNav);
        } else {
            this.instrumentationService.logGenericEvent(fastEvent.event, params);
        }
    }

    public setSearchCenterUrlReferral(searchCenterUrlReferral: string) {
        this.searchCenterUrlReferral = searchCenterUrlReferral;
    }

    // This method logs when search sessions start.
    // Each main search action continues a session until a timer has signified that the session has ended
    private checkForSessionTimeout() {
        if (!this.sessionStarted) {
            this.sessionStarted = true;
            const params = {};
            params[InstrumentationParam.ReferralType] = this.searchCenterUrlReferral;

            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.SearchSessionStarted, params);
        }

        // Clear out old timeout if one exists
        clearTimeout(this.sessionTimeout);

        this.sessionTimeout = setTimeout(() => {
            this.sessionStarted = false;
        }, this.sessionExpirationTimeInMilliseconds);
    }

    private getReferralFromEventData(eventData: string, event: FastInstrumentationEvent): string {
        if (event.searchResultType === SearchResultType.Person) {
            return OtherKnownReferralNames.People.toString();
        }

        if (event.searchResultType === SearchResultType.Site || event.searchResultType === SearchResultType.News) {
            return KnownWorkloadReferralNames.SharePoint;
        }

        if (!eventData) {
            return '';
        }
        eventData = eventData.toLocaleLowerCase();

        if (eventData.indexOf('excel') !== -1) {
            return KnownWorkloadReferralNames.Excel;
        }

        if (eventData.indexOf('powerpoint') !== -1) {
            return KnownWorkloadReferralNames.PowerPoint;
        }

        if (eventData.indexOf('word') !== -1) {
            return KnownWorkloadReferralNames.Word;
        }

        if (eventData.indexOf('onenote') !== -1) {
            return KnownWorkloadReferralNames.OneNote;
        }

        if (eventData.indexOf('pdf') !== -1) {
            return OtherKnownReferralNames.PDF.toString();
        }

        return '';
    }
}
